import React, { Component } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { TextField, MenuItem, CircularProgress } from '@material-ui/core';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CardLayout from "../widgets/Card.jsx";
import ViewUtils from '../ViewUtils.js';
import { BarChart, LineChart, PieChart, REPORT_CONFIG } from '../APPSummaryDashboardCharts';
import SummaryTable from '../APPSummaryDashboardCharts/SummaryTable'; 
import "../APPSummaryDashboardCharts/DashboardCharts.css";

const utils = new ViewUtils();

class APPSummaryDashboard extends Component {
    initialState = { 
        FinancialYearID: 0, 
        QuarterID: 0, 
        reportToShow: '', 
        totalCount: 0, 
        viewType: 'bar', 
        rawData: [], 
        exportLoading: false, 
        showLegend: true 
    };
    
    state = { ...this.initialState, lookupFinArray: [] };

    componentDidMount() {
        utils.invokeUrl("api/sims/APPSummaryDashboard/GetLookupList?listId=APPFinancialYear", (res) => {
            this.setState({ lookupFinArray: res || [] });
        });
    }

    handleReset = () => this.setState({ ...this.initialState, lookupFinArray: this.state.lookupFinArray });
    
    handleDataLoaded = (total, data) => {
        this.setState({ totalCount: total, rawData: data });
    }

    handleChartClick = (reportKey, qId = 0) => {
    this.setState({ 
        reportToShow: reportKey, 
        QuarterID: qId, 
        rawData: [] 
    });
}

    handleExport = (filterValue = "") => {
        const { FinancialYearID, QuarterID, reportToShow } = this.state;
        const config = REPORT_CONFIG[reportToShow];
        if (!config || !FinancialYearID) return;
        
        this.setState({ exportLoading: true });
        const params = [
            { Name: "FinancialYearID", Value: FinancialYearID },
            { Name: "ExportToExcel", Value: filterValue ? "Yes" : "All" },
            { Name: "Filter", Value: filterValue }
        ];

        if (config.hasQuarterFilter && QuarterID) {
            params.push({ Name: "QuarterID", Value: QuarterID });
        }

        const requestUrl = `${config.excelUrl}?request=` + utils.doGetRequestJson(0, 0, params, true);
        
        utils.invokeUrl(requestUrl, (data) => {
            const result = typeof data === "string" ? JSON.parse(data) : data;
            if (result && result.Base64) {
                const link = document.createElement('a');
                link.href = "data:" + result.Type + ";base64," + result.Base64;
                link.download = result.Name || 'Export.xlsx';
                link.click();
            }
            this.setState({ exportLoading: false });
        }, () => this.setState({ exportLoading: false }));
    }

    render() {
        const { FinancialYearID, QuarterID, reportToShow, viewType, rawData, exportLoading, lookupFinArray, showLegend } = this.state;
        
        const activeConfig = REPORT_CONFIG[reportToShow];
        const isDeepDive = FinancialYearID !== 0 && reportToShow !== '';
        const isOverview = FinancialYearID !== 0 && reportToShow === '';

        return (
            <CardLayout title="">
                <div className="container-fluid dashboard-container">
                    {/* --- NAVIGATION HEADER --- */}
                    <div className="dashboard-nav-header animate-slide-down">
                        <div className="d-flex align-items-center">
                            <p className="breadcrumbs">{"APP Summary Dashboard"}</p>
                            {isDeepDive && (
                                <React.Fragment>
                                    <span className="nav-separator">/</span>
                                    <span className="nav-active-report">{activeConfig.title}</span>
                                </React.Fragment>
                            )}
                        </div>
                        
                        {isDeepDive && (
                            <Button 
                                variant="contained" 
                                className="back-grid-btn"
                                onClick={() => this.setState({ reportToShow: '', QuarterID: 0, rawData: [] })}
                            >
                                <ArrowBackIcon className="mr-2" style={{ fontSize: 16 }} />
                                Back to Overview
                            </Button>
                        )}
                    </div>
                    
                    {/* --- FILTERS --- */}
                    <Row className="mb-4 align-items-end animate-slide-down">
                        <Col md={3}>
                            <TextField 
                                select fullWidth label="Financial Year" variant="outlined" size="small" 
                                value={FinancialYearID} 
                                onChange={(e) => this.setState({ ...this.initialState, FinancialYearID: e.target.value, lookupFinArray })}
                            >
                                <MenuItem value={0}>Select Year</MenuItem>
                                {lookupFinArray.map(item => <MenuItem key={item.Id} value={item.Id}>{item.Description}</MenuItem>)}
                            </TextField>
                        </Col>

                        <Col md={3}>
                            <TextField 
                                select fullWidth label="Dashboard View" variant="outlined" size="small" 
                                disabled={!FinancialYearID} 
                                value={reportToShow} 
                                onChange={(e) => this.setState({ reportToShow: e.target.value, QuarterID: 0, rawData: [] })}
                                SelectProps={{
                                    displayEmpty: true
                                }}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            >
                                <MenuItem value="">All Summaries (Grid)</MenuItem>
                                {Object.keys(REPORT_CONFIG).map(k => <MenuItem key={k} value={k}>{REPORT_CONFIG[k].title}</MenuItem>)}
                            </TextField>
                        </Col>

                        {/* FIXED: Removed Optional Chaining (?.) for legacy compiler support */}
                        {isDeepDive && activeConfig && activeConfig.hasQuarterFilter && (
                            <Col md={2}>
                                <TextField 
                                    select fullWidth label="Select Quarter" variant="outlined" size="small" 
                                    value={QuarterID} 
                                    onChange={(e) => this.setState({ QuarterID: e.target.value, rawData: [] })}
                                >
                                    <MenuItem value={0}>Select Quarter</MenuItem>
                                    {[1, 2, 3, 4].map(q => <MenuItem key={q} value={q}>Quarter {q}</MenuItem>)}
                                </TextField>
                            </Col>
                        )}

                        <Col className="text-right">
                            <Button variant="secondary" className="dashboard-btn" onClick={this.handleReset}>
                                <RotateLeftIcon className="mr-2"/> Reset
                            </Button>
                        </Col>
                    </Row>

                    {FinancialYearID !== 0 && (
                        <div className="d-flex justify-content-between align-items-center mb-3 animate-fade-in">
                            <div className="chart-switcher-pills">
                                <Button className={viewType === 'bar' ? 'active' : ''} onClick={() => this.setState({ viewType: 'bar' })}>Bar</Button>
                                <Button className={viewType === 'line' ? 'active' : ''} onClick={() => this.setState({ viewType: 'line' })}>Line</Button>
                                <Button className={viewType === 'pie' ? 'active' : ''} onClick={() => this.setState({ viewType: 'pie' })}>Pie</Button>
                            </div>
                            <Button variant="outline-info" size="small" onClick={() => this.setState({ showLegend: !showLegend })}>
                                {showLegend ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                <span className="ml-1">{showLegend ? "Hide Legends" : "Show Legends"}</span>
                            </Button>
                        </div>
                    )}

                    <hr />

                    {/* OVERVIEW GRID */}
                    {isOverview && (
                        <Row className="animate-fade-in">
                            {Object.keys(REPORT_CONFIG).map((key) => {
                                const config = REPORT_CONFIG[key];
                                
                                // Scenario: Quarterly Reports (Single Card with Internal Switcher)
                                if (config.hasQuarterFilter) {
                                    // Use the component's state or a default to track the 'Overview Quarter'
                                    // For simplicity, we'll use a local 'activeTab' logic or use this.state.QuarterID
                                    const activeQ = this.state["q_tab_" + key] || 1;

                                    return (
                                        <Col md={6} key={key} className="mb-4">
                                            <div className="chart-dark-theme">
                                                <div className="card shadow-lg">
                                                    <div className="card-header bg-transparent border-0 d-flex justify-content-between align-items-center pt-3 px-4">
                                                        <div onClick={() => this.handleChartClick(key, activeQ)} style={{cursor: 'pointer'}}>
                                                            <h6 className="text-white mb-0">{config.title}</h6>
                                                            <small className="text-info">Click to expand Quarter {activeQ}</small>
                                                        </div>
                                                        
                                                        {/* Mini Tab Switcher inside the Header */}
                                                        <div className="mini-q-switcher">
                                                            {[1, 2, 3, 4].map(q => (
                                                                <button 
                                                                    key={q}
                                                                    className={activeQ === q ? 'active' : ''}
                                                                    onClick={() => this.setState({ ["q_tab_" + key]: q })}
                                                                >
                                                                    Q{q}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <div className="card-body" style={{ minHeight: '350px' }}>
                                                        <BarChart 
                                                            showLegend={showLegend} 
                                                            FinancialYearID={FinancialYearID} 
                                                            reportName={key} 
                                                            QuarterID={activeQ} 
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                    );
                                }

                                // Standard Annual Reports (Single Card)
                                return (
                                    <Col md={6} key={key} className="mb-4">
                                        <div className="chart-dark-theme clickable-card" onClick={() => this.handleChartClick(key, 0)}>
                                            <div className="card shadow-lg">
                                                <div className="card-header bg-transparent border-0 d-flex justify-content-between pt-3 px-4">
                                                    <h6 className="text-white mb-0">{config.title}</h6>
                                                    <FullscreenIcon style={{ color: '#00d4ff', opacity: 0.6 }} />
                                                </div>
                                                <div className="card-body" style={{ minHeight: '350px' }}>
                                                    <BarChart showLegend={showLegend} FinancialYearID={FinancialYearID} reportName={key} QuarterID={0} />
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>
)}

                    {/* DEEP DIVE */}
                    {isDeepDive && (
                        <div className="animate-fade-in">
                            <div className="chart-dark-theme mb-4">
                                <div className="card shadow-lg">
                                    <div className="card-header bg-transparent border-0 d-flex justify-content-between pt-3 px-4">
                                        <h5 className="text-white mb-0">{activeConfig.title} Detailed Analysis</h5>
                                        <Button size="small" variant="primary" onClick={() => this.handleExport("")} disabled={exportLoading}>
                                            <CloudDownloadIcon className="mr-1" /> {exportLoading ? "Exporting..." : "Export Excel"}
                                        </Button>
                                    </div>
                                    <div className="card-body" style={{ minHeight: '500px' }}>
                                        {viewType === 'bar' && <BarChart showLegend={showLegend} QuarterID={QuarterID} FinancialYearID={FinancialYearID} reportName={reportToShow} onDataLoaded={this.handleDataLoaded} onPointClick={this.handleExport} />}
                                        {viewType === 'line' && <LineChart showLegend={showLegend} QuarterID={QuarterID} FinancialYearID={FinancialYearID} reportName={reportToShow} onDataLoaded={this.handleDataLoaded} onPointClick={this.handleExport} />}
                                        {viewType === 'pie' && <PieChart showLegend={showLegend} QuarterID={QuarterID} FinancialYearID={FinancialYearID} reportName={reportToShow} onDataLoaded={this.handleDataLoaded} onPointClick={this.handleExport} />}
                                    </div>
                                </div>
                            </div>
                            {rawData.length > 0 && <SummaryTable data={rawData} reportName={reportToShow} QuarterID={QuarterID} />}
                        </div>
                    )}

                    {!FinancialYearID && (
                        <div className="text-center py-5">
                            <h4 className="text-muted">Select a Financial Year to view the APP Summary Performance</h4>
                        </div>
                    )}
                </div>
            </CardLayout>
        );
    }
}

export default APPSummaryDashboard;